/**
 * Barra de ferramentas da listagem do catálogo (Entrega 3, item 3.1).
 *
 * Só APRESENTAÇÃO: busca, filtros e paginação client-side sobre o dataset já
 * carregado (ver catalog/list-view.ts). Não há `fetch` aqui nem recálculo de
 * regra — o estado vive na URL (`useSearchParams` na tela), então a listagem é
 * navegável e deep-linkável.
 *
 * Acessibilidade (régua §2):
 *  - todo controle tem `label` associado (`htmlFor`/`id`) e nome acessível;
 *  - a paginação é um `<nav aria-label>` com `aria-current="page"` na página
 *    corrente — cor nunca é o único indicador (texto "Página X de Y" + estado);
 *  - alvos ≥44px no toque (min-h/min-w).
 *
 * Migração para `.tsx` (Cluster B — Catálogo & Estoque, redesign por perfil):
 * mesmo markup/contrato de antes, só a sintaxe `createElement` → JSX. Import
 * de `React` explícito — ver nota equivalente em `layout/AppShell.tsx`.
 */
import React, { type ChangeEvent, type ReactElement } from "react";
import { ChevronLeft, ChevronRight, Search, X } from "lucide-react";
import type { CatalogListFilters, CatalogPage } from "../catalog/list-view.js";

export interface TableToolbarProps {
  filters: CatalogListFilters;
  categories: string[];
  suppliers: string[];
  /** Aplica um patch de filtros à URL; reseta a página quando o recorte muda. */
  onChange: (patch: Partial<CatalogListFilters>) => void;
  /** Volta ao estado sem filtro/busca. */
  onReset: () => void;
  /** `true` quando há filtro/busca ativo (habilita o "limpar"). */
  active: boolean;
}

const fieldClass =
  "min-h-[44px] w-full rounded-control border border-border-strong bg-surface px-3 py-2 " +
  "text-sm text-ink placeholder:text-muted focus-visible:outline-none";

function LabelledField({ id, label, control }: { id: string; label: string; control: ReactElement }): ReactElement {
  return (
    <div className="flex min-w-0 flex-col gap-1">
      <label htmlFor={id} className="text-xs font-semibold uppercase tracking-wide text-muted">
        {label}
      </label>
      {control}
    </div>
  );
}

export function TableToolbar({ filters, categories, suppliers, onChange, onReset, active }: TableToolbarProps): ReactElement {
  return (
    <div
      className="table-toolbar flex flex-col gap-4 rounded-panel border border-border bg-surface p-4 shadow-s1"
      data-toolbar="catalogo"
      role="search"
      aria-label="Buscar e filtrar o catálogo"
    >
      {/* Busca em linha própria (foco primário da barra). */}
      <LabelledField
        id="catalogo-busca"
        label="Buscar produto"
        control={
          <div className="relative">
            <Search
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted"
              aria-hidden
              focusable={false}
            />
            <input
              id="catalogo-busca"
              type="search"
              name="busca"
              value={filters.search}
              placeholder="SKU, nome comercial, categoria ou fornecedor"
              className={`${fieldClass} pl-9`}
              data-filter-control="busca"
              onChange={(event: ChangeEvent<HTMLInputElement>) => onChange({ search: event.target.value })}
            />
          </div>
        }
      />
      {/* Filtros em grade responsiva (empilha em 360). */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <LabelledField
          id="catalogo-categoria"
          label="Categoria"
          control={
            <select
              id="catalogo-categoria"
              name="categoria"
              value={filters.category ?? ""}
              className={fieldClass}
              data-filter-control="categoria"
              onChange={(event: ChangeEvent<HTMLSelectElement>) => onChange({ category: event.target.value || null })}
            >
              <option value="">Todas as categorias</option>
              {categories.map((category) => (
                <option key={category} value={category}>
                  {category}
                </option>
              ))}
            </select>
          }
        />
        <LabelledField
          id="catalogo-fornecedor"
          label="Fornecedor"
          control={
            <select
              id="catalogo-fornecedor"
              name="fornecedor"
              value={filters.supplier ?? ""}
              className={fieldClass}
              data-filter-control="fornecedor"
              onChange={(event: ChangeEvent<HTMLSelectElement>) => onChange({ supplier: event.target.value || null })}
            >
              <option value="">Todos os fornecedores</option>
              {suppliers.map((supplier) => (
                <option key={supplier} value={supplier}>
                  {supplier}
                </option>
              ))}
            </select>
          }
        />
        <LabelledField
          id="catalogo-publicado"
          label="Publicação na loja"
          control={
            <select
              id="catalogo-publicado"
              name="publicado"
              value={filters.published === null ? "" : filters.published ? "sim" : "nao"}
              className={fieldClass}
              data-filter-control="publicado"
              onChange={(event: ChangeEvent<HTMLSelectElement>) =>
                onChange({ published: event.target.value === "sim" ? true : event.target.value === "nao" ? false : null })
              }
            >
              <option value="">Publicados e não</option>
              <option value="sim">Publicados</option>
              <option value="nao">Não publicados</option>
            </select>
          }
        />
        <LabelledField
          id="catalogo-disponibilidade"
          label="Disponibilidade"
          control={
            <select
              id="catalogo-disponibilidade"
              name="disponibilidade"
              value={filters.availability ?? ""}
              className={fieldClass}
              data-filter-control="disponibilidade"
              onChange={(event: ChangeEvent<HTMLSelectElement>) =>
                onChange({ availability: (event.target.value || null) as CatalogListFilters["availability"] })
              }
            >
              <option value="">Qualquer disponibilidade</option>
              <option value="disponivel">Disponível</option>
              <option value="critico">Estoque crítico</option>
              <option value="indisponivel">Indisponível</option>
            </select>
          }
        />
      </div>
      {active ? (
        <div className="flex">
          <button
            type="button"
            onClick={onReset}
            data-filter-control="limpar"
            className={
              "inline-flex min-h-[44px] items-center gap-2 rounded-control border border-border-strong " +
              "bg-surface px-3 py-2 text-sm font-semibold text-brand-text transition-colors duration-fast " +
              "ease-s2 hover:bg-surface-soft focus-visible:outline-none"
            }
          >
            <X className="h-4 w-4" aria-hidden focusable={false} />
            Limpar filtros
          </button>
        </div>
      ) : null}
    </div>
  );
}

export interface PaginationProps {
  page: CatalogPage;
  /** Devolve a URL (query string) para uma página — mantém a listagem navegável. */
  hrefForPage: (pageNumber: number) => string;
}

/** Botão de navegação (anterior/próxima) da paginação — habilitado ou `<span>` desabilitado. */
function NavButton({
  label,
  target,
  disabled,
  rel,
  direction,
  hrefForPage
}: {
  label: string;
  target: number;
  disabled: boolean;
  rel: string;
  direction: "prev" | "next";
  hrefForPage: (pageNumber: number) => string;
}): ReactElement {
  const Icon = direction === "prev" ? ChevronLeft : ChevronRight;
  const iconEl = <Icon className="h-4 w-4" aria-hidden focusable={false} />;
  const content =
    direction === "prev" ? (
      <>
        {iconEl}
        {label}
      </>
    ) : (
      <>
        {label}
        {iconEl}
      </>
    );
  return disabled ? (
    <span
      className="inline-flex min-h-[44px] items-center gap-1 rounded-control border border-border px-3 py-2 text-sm font-medium text-muted"
      aria-disabled
    >
      {content}
    </span>
  ) : (
    <a
      href={hrefForPage(target)}
      rel={rel}
      className="inline-flex min-h-[44px] items-center gap-1 rounded-control border border-border-strong px-3 py-2 text-sm font-medium text-body transition-colors duration-fast ease-s2 hover:bg-surface-soft focus-visible:outline-none"
    >
      {content}
    </a>
  );
}

/**
 * Paginação acessível: resumo textual ("X–Y de N") + navegação com
 * `aria-current="page"`. O estado é texto + atributo, nunca só cor.
 */
export function TablePagination({ page, hrefForPage }: PaginationProps): ReactElement {
  const { page: current, totalPages, from, to, total } = page;
  const summary = total === 0 ? "Nenhum produto no recorte" : `Mostrando ${from}–${to} de ${total} produto(s)`;

  // Janela compacta de páginas ao redor da corrente (lista densa, sem poluir).
  const windowStart = Math.max(1, current - 2);
  const windowEnd = Math.min(totalPages, current + 2);
  const pageNumbers: number[] = [];
  for (let n = windowStart; n <= windowEnd; n += 1) pageNumbers.push(n);

  return (
    <nav className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between" aria-label="Paginação do catálogo" data-pagination="catalogo">
      <p className="text-sm text-body" data-pagination-summary={`${from}-${to}-${total}`}>
        {summary}
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <NavButton label="Anterior" target={current - 1} disabled={current <= 1} rel="prev" direction="prev" hrefForPage={hrefForPage} />
        <ul className="flex flex-wrap items-center gap-1" role="list">
          {pageNumbers.map((n) => (
            <li key={n}>
              {n === current ? (
                <span
                  className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-control border border-brand bg-brand-wash px-3 py-2 text-sm font-bold text-ink"
                  aria-current="page"
                  data-page-current={String(n)}
                >
                  {/* Texto acessível completo; o número visível é reforço. */}
                  <span className="sr-only">{`Página ${n}, página atual`}</span>
                  <span aria-hidden>{String(n)}</span>
                </span>
              ) : (
                <a
                  href={hrefForPage(n)}
                  aria-label={`Ir para a página ${n}`}
                  className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-control border border-border-strong px-3 py-2 text-sm font-medium text-body transition-colors duration-fast ease-s2 hover:bg-surface-soft focus-visible:outline-none"
                >
                  {String(n)}
                </a>
              )}
            </li>
          ))}
        </ul>
        <NavButton label="Próxima" target={current + 1} disabled={current >= totalPages} rel="next" direction="next" hrefForPage={hrefForPage} />
      </div>
    </nav>
  );
}
